/**
 * Pure helpers powering Slice 3b compare mode (parent run vs child run).
 *
 * The preview API returns row-level confidence (not per-cell) and rows are
 * not guaranteed to share an index between parent and child — refinement
 * can drop or insert lines. We align by a cheap composite key derived from
 * stable identifiers we know DocAI surfaces (TrxNumber + Description +
 * BillFrom). When a key is missing we fall back to a normalised
 * description-only key so the diff still produces useful signal.
 */
const KEY_COLUMNS = ['TrxNumber', 'Description', 'BillFrom'];
function normaliseValue(v) {
    return (v ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
}
/**
 * Build a stable row-match key. Prefers `TrxNumber|Description|BillFrom`;
 * falls back to just the normalised Description when any key column is
 * blank. Public for unit testing.
 */
export function rowMatchKey(row) {
    const cells = row.cells ?? {};
    const parts = KEY_COLUMNS.map((c) => normaliseValue(cells[c]));
    if (parts.every((p) => p === '')) {
        // No identifying columns at all — fall back to the row's full cell
        // signature so we don't collapse every blank row into a single key.
        return JSON.stringify(cells);
    }
    if (parts.some((p) => p === '')) {
        return `desc:${normaliseValue(cells.Description)}`;
    }
    return parts.join('|');
}
/**
 * Classify a single matched row. Confidence-only changes are surfaced
 * separately so they don't flood the diff banner — operators care about
 * value drift, confidence drift is informational.
 */
export function classifyMatchedRow(parent, child) {
    const cols = new Set([
        ...Object.keys(parent.cells ?? {}),
        ...Object.keys(child.cells ?? {}),
    ]);
    const changedColumns = [];
    for (const c of cols) {
        const a = normaliseValue(parent.cells?.[c]);
        const b = normaliseValue(child.cells?.[c]);
        if (a !== b)
            changedColumns.push(c);
    }
    if (changedColumns.length > 0) {
        return { classification: 'value-changed', changedColumns };
    }
    const pc = typeof parent.confidence === 'number' ? parent.confidence : undefined;
    const cc = typeof child.confidence === 'number' ? child.confidence : undefined;
    if (pc !== undefined && cc !== undefined && cc > pc + 1e-6) {
        return { classification: 'confidence-only', changedColumns: [] };
    }
    return { classification: 'unchanged', changedColumns: [] };
}
/**
 * Diff parent vs child row sets. Order of returned rows preserves the
 * child's iteration order first (with kept + changed + new), then parent-
 * only removed rows last — this matches the side-by-side rendering which
 * walks the union once.
 */
export function diffPreviewRows(parentRows, childRows) {
    const parentByKey = new Map();
    for (const r of parentRows) {
        const k = rowMatchKey(r);
        // First write wins so duplicate keys still produce a stable match.
        if (!parentByKey.has(k))
            parentByKey.set(k, r);
    }
    const childKeys = new Set();
    const diffs = [];
    const counters = {
        changed: 0,
        confidenceOnly: 0,
        added: 0,
        removed: 0,
        unchanged: 0,
    };
    for (const child of childRows) {
        const key = rowMatchKey(child);
        childKeys.add(key);
        const parent = parentByKey.get(key);
        if (!parent) {
            diffs.push({ key, classification: 'new', changedColumns: [], child });
            counters.added += 1;
            continue;
        }
        const { classification, changedColumns } = classifyMatchedRow(parent, child);
        diffs.push({ key, classification, changedColumns, parent, child });
        if (classification === 'value-changed')
            counters.changed += 1;
        else if (classification === 'confidence-only')
            counters.confidenceOnly += 1;
        else
            counters.unchanged += 1;
    }
    for (const [key, parent] of parentByKey) {
        if (!childKeys.has(key)) {
            diffs.push({ key, classification: 'removed', changedColumns: [], parent });
            counters.removed += 1;
        }
    }
    return { diffs, counters };
}
/**
 * Tiny pretty-printer for the counter strip — keeps formatting decisions
 * out of the React component so we can unit-test the copy.
 */
export function formatCompareCounters(c) {
    const parts = [];
    parts.push(`${c.changed} changed`);
    parts.push(`${c.confidenceOnly} confidence-only`);
    parts.push(`${c.added} new`);
    parts.push(`${c.removed} removed`);
    return parts.join(' · ');
}
//# sourceMappingURL=extractor-compare.js.map